import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { z } from 'zod/v4';

import { IntegrationAuth, IntegrationConnector, IntegrationConnectorCategory } from '../../lib';

export type FreshserviceConnectorConfig = typeof FreshserviceConnector._.Config;

const freshserviceAccountHost = /^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.freshservice\.com$/;

/* The messages name their translation keys (params.i18n) for the settings form; the English
 * stays for errors raised outside it. */
// Any text that is not the account URL, a bare host included, gets the Freshservice message, not a generic URL one.
const freshserviceBaseUrlSchema = z
  .string()
  .refine(
    (value) => {
      if (!URL.canParse(value)) return false;
      const url = new URL(value);
      return url.protocol === 'https:' && freshserviceAccountHost.test(url.hostname);
    },
    {
      message: 'Use an https://<account>.freshservice.com URL.',
      params: { i18n: 'configureForm.validation.freshserviceUrl' },
      abort: true,
    },
  )
  .refine(
    (value) => {
      const url = new URL(value);
      // Empty query and fragment delimiters are absent from URL.search and URL.hash.
      return url.port === '' && url.href === `${url.origin}/`;
    },
    {
      message:
        'Freshservice account URLs must not include credentials, custom ports, paths, queries, or fragments.',
      params: { i18n: 'configureForm.validation.accountUrlOnly' },
    },
  );

export const FreshserviceConnector = IntegrationConnector.make({
  provider: Integration_Names_Enum.Freshservice,
  logoPath: 'images/freshservice-logo.svg',
  categories: [IntegrationConnectorCategory.ITSM],
  auths: IntegrationAuth.group([
    IntegrationAuth.manual({
      name: Integration_Auth_Names_Enum.ApiKey,
      configSchema: z.object({
        baseUrl: freshserviceBaseUrlSchema,
      }),
      encryptedConfigSchema: z.object({
        apiKey: z.string().min(1),
      }),
    }),
  ]),
});
